import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Check, Loader2 } from "lucide-react";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 15;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const txnid = params.get("txnid") || params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [record, setRecord] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!txnid) { setStatus("failed"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${txnid}`);
        setRecord(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refresh(); // pick up updated referral_credit_inr etc.
          return;
        }
        if (data.status === "failed" || data.payment_status === "failed" || data.status === "hash_failed") {
          setStatus("failed");
          return;
        }
      } catch { /* retry */ }
      if (attemptsRef.current >= MAX_ATTEMPTS) { setStatus("failed"); return; }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => { stop = true; };
  }, [txnid, refresh]);

  return (
    <div data-testid="payment-success-page" className="min-h-[80vh] flex items-center">
      <div className="max-w-[900px] mx-auto px-6 md:px-10 py-20 w-full">
        {status === "checking" && (
          <div>
            <Loader2 size={40} className="animate-spin text-novya-accent" />
            <h1 className="mt-8 font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] text-novya-ink">
              Confirming your payment…
            </h1>
            <p className="mt-4 text-novya-muted">This usually takes a few seconds.</p>
          </div>
        )}
        {status === "paid" && (
          <div>
            <div className="w-14 h-14 rounded-full bg-novya-accent flex items-center justify-center">
              <Check size={28} className="text-white" />
            </div>
            <h1 className="mt-8 font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] text-novya-ink">
              You&apos;re in the cohort<span className="text-novya-accent">.</span>
            </h1>
            <p className="mt-6 text-novya-muted max-w-xl leading-relaxed">
              Payment received for <span className="text-novya-ink font-medium">{record?.program_title}</span> · {formatINR(record?.amount || 0)}. A receipt has been emailed to you and your cohort kickoff details will follow within 24 hours.
            </p>
            <div className="mt-4 font-mono text-[11px] tracking-[0.2em] uppercase text-novya-muted">
              Transaction · {txnid}
            </div>
            <div className="mt-10 flex gap-4">
              <Link to="/dashboard" data-testid="success-dashboard-btn" className="px-8 py-4 bg-novya-ink text-white font-medium hover:bg-novya-accent transition-colors">
                Go to dashboard
              </Link>
              <Link to="/programs" data-testid="success-programs-btn" className="px-8 py-4 border border-novya-ink text-novya-ink font-medium hover:bg-novya-ink hover:text-white transition-colors">
                Browse programs
              </Link>
            </div>
          </div>
        )}
        {status === "failed" && (
          <div>
            <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] text-novya-ink">
              Couldn&apos;t confirm payment.
            </h1>
            <p className="mt-6 text-novya-muted max-w-xl">
              If your card was charged we&apos;ll reflect it in your dashboard shortly. Otherwise try again.
            </p>
            <div className="mt-10 flex gap-4">
              <Link to="/programs" data-testid="failed-retry-btn" className="px-8 py-4 bg-novya-ink text-white font-medium hover:bg-novya-accent transition-colors">
                Try again
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
